import type { ClientEvents } from "@neus/shared";

type ClientEvent = ClientEvents["events"][number];

const SESSION_KEY = "neus-session";

/** A random id for this browser tab: anonymous, kept in sessionStorage, never a cookie. */
function sessionId(): string | null {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID().replace(/-/g, "");
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return null; // storage blocked (e.g. some private modes): don't track
  }
}

/**
 * Report storefront activity (product views, add-to-carts) for the weekly digest. Best effort:
 * never throws, skipped when the browser sends Do Not Track, and sent with sendBeacon so it
 * survives navigation. Purchases are recorded by the server.
 */
export function track(...events: ClientEvent[]): void {
  try {
    if (navigator.doNotTrack === "1") return;
    const id = sessionId();
    if (!id || !events.length) return;
    const body = JSON.stringify({ sessionId: id, events } satisfies ClientEvents);
    // text/plain is a CORS-safelisted type, so every browser accepts it for a beacon; the
    // server parses the body as JSON regardless.
    let queued = false;
    try {
      queued = navigator.sendBeacon?.("/api/events", new Blob([body], { type: "text/plain" })) ?? false;
    } catch {
      queued = false;
    }
    if (!queued) {
      void fetch("/api/events", { method: "POST", body, keepalive: true }).catch(() => undefined);
    }
  } catch {
    // Metrics must never break the page.
  }
}
